import React, { useMemo, useState } from 'react';
import {
  AccordionDetails,
  Alert,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { AccountData, PassOrderPageData, PassOut, PassScheduleSelectionDTO, PassType } from '../../../../api';
import { Accordion, AccordionSummary } from '../../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { getOrderPage } from '../../../../utils/AccountUtils';
import { useLanguage } from '../../../../common/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { formatSecondsToTime, getDayName } from '../../../../utils/weekdayFormat';
import { EditPassFormValues, PassDetailPanels } from '../../PassDetailTypes';
import { ScheduleSelectionRow } from './ScheduleSelectionRow';

type Props = {
  pass: PassOut;
  passOrderPage?: PassOrderPageData;
  expandedPanel: PassDetailPanels | undefined;
  onPanelChange: (panel: PassDetailPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onChangeScheduleClick?: () => void;
  onReleaseScheduleClick?: () => void;
};

const isCurrentSelection = (selection: PassScheduleSelectionDTO): boolean => {
  if (!selection.effective_until) return true;
  return moment(selection.effective_until).isSameOrAfter(moment(), 'day');
};

export const SchedulablePassSchedulePanel = ({
  pass,
  passOrderPage,
  expandedPanel,
  onPanelChange,
  onChangeScheduleClick,
  onReleaseScheduleClick,
}: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { values, setFieldValue } = useFormikContext<EditPassFormValues>();
  const timeFormat = getTimeFormat(account);
  const dateFormat = getDateFormat(account).longDateFormat;
  const selections = pass.schedule_selections ?? [];
  const releasedFrom = pass.schedule_released_from;
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const formatDateRange = (selection: PassScheduleSelectionDTO): string | undefined => {
    const from = selection.effective_from ? moment(selection.effective_from).format(dateFormat) : undefined;
    const until = selection.effective_until ? moment(selection.effective_until).format(dateFormat) : undefined;
    if (from && until) return `${from} → ${until}`;
    if (from) return t('passes.schedule_effective_from', { date: from });
    if (until) return t('passes.schedule_effective_until', { date: until });
    return undefined;
  };

  const { current, history } = useMemo(() => {
    const cur: PassScheduleSelectionDTO[] = [];
    const hist: PassScheduleSelectionDTO[] = [];
    for (const s of selections) {
      if (isCurrentSelection(s)) cur.push(s);
      else hist.push(s);
    }
    return { current: cur, history: hist };
  }, [selections]);

  const summary = current
    .map((s) => `${getDayName(s.weekday, language)} ${formatSecondsToTime(s.start_time, timeFormat)}`)
    .join(' · ');

  const isPunchBased = passOrderPage?.pass_type === PassType.PUNCH_BASED;
  const showHoldToggle = isPunchBased && current.length >= 1;

  const renderRow = (selection: PassScheduleSelectionDTO, keyPrefix: string, i: number) => {
    const targetPage = getOrderPage(account, selection.order_page_id);
    const pageLabel = targetPage?.label?.[language] || targetPage?.label?.en || selection.order_page_id;
    return (
      <ScheduleSelectionRow
        key={`${keyPrefix}-${selection.order_page_id}-${selection.weekday}-${selection.start_time}-${i}`}
        weekdayLabel={getDayName(selection.weekday, language)}
        timeLabel={formatSecondsToTime(selection.start_time, timeFormat)}
        pageLabel={pageLabel}
        dateRangeLabel={formatDateRange(selection)}
      />
    );
  };

  return (
    <Accordion
      expandedPanelName={expandedPanel}
      panelName={PassDetailPanels.Schedule}
      onChange={onPanelChange}
      allOpenValue={PassDetailPanels.AllOpen}>
      <AccordionSummary
        title={t('passes.schedule_title')}
        summaryComponent={
          <Typography variant="body2" color="text.secondary" noWrap maxWidth="240px">
            {summary}
          </Typography>
        }
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={1}>
          {current.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('passes.schedule_none_yet')}
            </Typography>
          )}

          {current.map((selection, i) => renderRow(selection, 'current', i))}

          {releasedFrom && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('passes.schedule_released_from', { date: moment(releasedFrom).format(dateFormat) })}
            </Alert>
          )}

          <Grid container mt={1} gap={1}>
            {onChangeScheduleClick && (
              <Button variant="outlined" size="small" onClick={onChangeScheduleClick}>
                {current.length === 0 ? t('passes.attach_schedule_cta') : t('passes.change_schedule_cta')}
              </Button>
            )}
            {onReleaseScheduleClick && current.length > 0 && (
              <Button variant="outlined" size="small" color="warning" onClick={onReleaseScheduleClick}>
                {t(releasedFrom ? 'passes.release_schedule_change_cta' : 'passes.release_schedule_cta')}
              </Button>
            )}
          </Grid>

          {showHoldToggle && (
            <Grid container flexDirection="column" mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.permanent_schedule_hold}
                    onChange={(e) => setFieldValue('permanent_schedule_hold', e.target.checked)}
                  />
                }
                label={t('passes.permanent_schedule_hold_toggle')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1, marginInlineStart: '32px' }}>
                {t('passes.permanent_schedule_hold_helper')}
              </Typography>
            </Grid>
          )}

          {history.length > 0 && (
            <Grid container flexDirection="column" mt={2}>
              <Grid container alignItems="center" gap={0.5}>
                <IconButton
                  size="small"
                  onClick={() => setHistoryExpanded((v) => !v)}
                  aria-label={
                    historyExpanded ? t('auditlog.change_details.collapse') : t('auditlog.change_details.expand')
                  }>
                  {historyExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                <Typography variant="body2" fontWeight={600}>
                  {t('passes.schedule_history_title')}
                </Typography>
              </Grid>
              <Collapse in={historyExpanded} unmountOnExit>
                <Grid container flexDirection="column" gap={1} mt={1}>
                  {history.map((selection, i) => renderRow(selection, 'history', i))}
                </Grid>
              </Collapse>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
